import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ethers } from 'ethers';
import { provider, ethersUtils, createContract, getAccounts, initializeEthers } from './EthersSetup';
import CoinFlip from '../deployedContractsInfo/CoinFlip.json';
import GameMakerConfig from '../deployedContractsInfo/GameMaker.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import blockchainDataManager from '../services/BlockchainDataManager.js';
import { AddressDisplayLong } from './AddressDisplay.js';
import { TransactionLink } from './LinkDisplay';
import TopRightBar from './basics/TopRightBar';
import GameTransactionHistory from './basics/GameTransactionHistory';
import { FullGameSkeleton, TransactionPanelSkeleton } from './ui/SkeletonLoader';

const CoinFlipGame = () => {
  const { gameID = '1' } = useParams();
  const gameIDNumber = parseInt(gameID); // Convert to number for contract calls
  const [gameState, setGameState] = useState({
    player1: '',
    player2: '',
    status: '',
    gameFinished: false,
    lastMoveTime: 0,
    contribution1: 0,
    contribution2: 0,
    coinResult: null,
    winner: ''
  });

  const [userState, setUserState] = useState({ userAddress: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [uiReady, setUiReady] = useState(false);
  const [loadingStages, setLoadingStages] = useState({
    gameBasics: true,    // Player names, status, coin result
    userFeatures: true   // Wallet-dependent features
  });
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);

  // Memoized game events for GameTransactionHistory to prevent constant reloading
  const coinFlipGameEvents = useMemo(() => [
    { name: 'CoinFlipped', label: 'Coin Flipped' }
  ], []);

  // Contract state management for async creation
  const [readOnlyContract, setReadOnlyContract] = useState(null);
  const [gameMakerContract, setGameMakerContract] = useState(null);

  // Initialize read-only contract asynchronously
  useEffect(() => {
    const initReadOnlyContract = async () => {
      try {
        const contract = await createContract(contractAddresses.CoinFlip, CoinFlip.abi, false);
        setReadOnlyContract(contract);
      } catch (error) {
        console.warn('[CoinFlip] Failed to create read-only contract:', error);
        setReadOnlyContract(null);
      }
    };
    initReadOnlyContract();
  }, []);

  // Create GameMaker contract for transaction history
  useEffect(() => {
    const initGameMakerContract = async () => {
      try {
        const contract = await createContract(contractAddresses.GameMaker, GameMakerConfig.abi);
        setGameMakerContract(contract);
      } catch (error) {
        console.error('CoinFlip: Failed to create GameMaker contract:', error);
      }
    };
    initGameMakerContract();
  }, []);

  // Create stable contract instances
  const [coinFlipContract, setCoinFlipContract] = useState(null);
  const [ethersContract, setEthersContract] = useState(null);
  const [signerContract, setSignerContract] = useState(null);

  // Load transaction information helper

  // Staggered loading functions for different data stages
  const loadGameBasics = useCallback(async () => {
    try {
      const contractToUse = readOnlyContract || await createContract(contractAddresses.CoinFlip, CoinFlip.abi, false);
      if (!contractToUse) {
        console.log('[CoinFlip] No contract available yet, will retry when provider is ready');
        setLoadingStages(prev => ({ ...prev, gameBasics: false }));
        return;
      }

      // Execute all contract calls in parallel for better performance
      const [gameInstance, game, coinResult] = await Promise.all([
        contractToUse.getGameInstance(gameIDNumber),
        contractToUse.games(gameIDNumber),
        contractToUse.getGameState(gameIDNumber)
      ]);

      const player1 = gameInstance.player1;
      const player2 = gameInstance.player2;
      const status = gameInstance.status.toString();
      const gameFinished = !gameInstance.gameActive;
      const lastMoveTime = Number(gameInstance.lastMoveTime);
      const contribution1 = game.contribution1;
      const contribution2 = game.contribution2;

      // Determine winner based on status
      let winner = '';
      if (status === '3') { // GameEndedPlayer1Won
        winner = player1;
      } else if (status === '4') { // GameEndedPlayer2Won
        winner = player2;
      } else if (status === '2') { // GameEndedDraw
        winner = 'draw';
      }

      const newGameState = {
        player1,
        player2,
        status,
        gameFinished,
        lastMoveTime,
        contribution1,
        contribution2,
        coinResult,
        winner
      };

      setGameState(newGameState);
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
      console.log('[CoinFlip] Game basics loaded');
    } catch (error) {
      console.error('[CoinFlip] Failed to load game basics:', error);
      setErrorMessage('Failed to load game information');
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
    }
  }, [gameIDNumber, readOnlyContract]);


  const initializeWalletFeatures = useCallback(async () => {
    try {
      if (!userState.userAddress) {
        setLoadingStages(prev => ({ ...prev, userFeatures: false }));
        return;
      }

      // Create signer contract when wallet is available (CoinFlip is view-only, no moves needed)
      const signerContractInstance = await createContract(contractAddresses.CoinFlip, CoinFlip.abi, true);
      setSignerContract(signerContractInstance);

      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
      console.log('[CoinFlip] Wallet features initialized');
    } catch (error) {
      console.error('[CoinFlip] Failed to initialize wallet features:', error);
      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
    }
  }, [userState.userAddress]);

  // Load game data function
  const loadGameData = useCallback(async () => {
    if (!coinFlipContract) return;
    try {

      // Execute all contract calls in parallel for better performance
      const [gameInstance, game, coinResult] = await Promise.all([
        coinFlipContract.getGameInstance(gameIDNumber),
        coinFlipContract.games(gameIDNumber),
        coinFlipContract.getGameState(gameIDNumber)
      ]);

      const player1 = gameInstance.player1;
      const player2 = gameInstance.player2;
      const status = gameInstance.status.toString();
      const gameFinished = !gameInstance.gameActive;
      const lastMoveTime = Number(gameInstance.lastMoveTime);
      const contribution1 = game.contribution1;
      const contribution2 = game.contribution2;

      // Determine winner based on status
      let winner = '';
      if (status === '3') { // GameEndedPlayer1Won
        winner = player1;
      } else if (status === '4') { // GameEndedPlayer2Won
        winner = player2;
      } else if (status === '2') { // GameEndedDraw
        winner = 'draw';
      }

      setGameState({ 
        player1, 
        player2, 
        status, 
        gameFinished, 
        lastMoveTime, 
        contribution1, 
        contribution2,
        coinResult: Number(coinResult),
        winner
      });
      
      // Loading stages are managed by individual functions now
    } catch (error) {
      console.error('Failed to load game data:', error);
      if (error.message.includes("Game with ID doesn't exist")) {
        setErrorMessage("Game with ID doesn't exist");
      } else {
        setErrorMessage('Failed to load game data');
      }
      // Loading stages are managed by individual functions now
    }
  }, [coinFlipContract, gameID]);

  // Main initialization with staggered loading
  useEffect(() => {
    // Show UI immediately
    setUiReady(true);

    // Start loading game basics immediately (non-blocking)
    loadGameBasics();

    // Initialize wallet in background
    const initWallet = async () => {
      try {
        await initializeEthers();
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserState({ userAddress: accounts[0] });
        }
        setIsWalletInitialized(true);
      } catch (error) {
        console.error('CoinFlip: Failed to initialize wallet:', error);
        setIsWalletInitialized(true); // Still allow component to continue
      }
    };
    initWallet();
  }, [loadGameBasics]);

  // Initialize wallet features when wallet is ready
  useEffect(() => {
    if (isWalletInitialized) {
      initializeWalletFeatures();
    }
  }, [isWalletInitialized, initializeWalletFeatures]);

  // Retry loading game basics when contract becomes available
  useEffect(() => {
    if (readOnlyContract && loadingStages.gameBasics) {
      console.log('[CoinFlip] Contract now available, retrying game basics load');
      loadGameBasics();
    }
  }, [readOnlyContract, loadingStages.gameBasics, loadGameBasics]);


  // Set browser tab title for the game
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `CoinFlip #${gameID}`;

    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, [gameID]);

  // Auto-pause polling when game finishes
  useEffect(() => {
    const isGameFinished = gameState.gameFinished === true;
    window.dispatchEvent(new CustomEvent('setPollingPause', {
      detail: {
        isPaused: isGameFinished,
        reason: isGameFinished ? 'game-finished' : ''
      }
    }));
  }, [gameState.gameFinished]);

  // Create ethers contract for events when basic data is loaded
  useEffect(() => {
    if (!loadingStages.gameBasics && provider) {
      const ethersContract = new ethers.Contract(contractAddresses.CoinFlip, CoinFlip.abi, provider);
      setEthersContract(ethersContract);
      console.log('🔧 CoinFlip: Ethers contract created');
    }
  }, [loadingStages.gameBasics]);

  // Wallet handlers for TopRightBar
  const handleSwitchWallet = () => {
    // For individual game pages, we'll navigate back to main page for wallet switching
    window.location.href = '/';
  };

  const handleWalletDisconnect = async () => {
    try {
      const accounts = await getAccounts();
      setUserState({ userAddress: accounts[0] || '' });
    } catch (error) {
      console.error('Error after wallet disconnect:', error);
      window.location.href = '/';
    }
  };

  // Event handler for game state updates
  const handleGameUpdate = useCallback(async (eventName) => {
    console.log(`[CoinFlip] ${eventName} event detected, refreshing game state...`);
    // Load game data (transaction history handled by GameTransactionHistory component)
    await loadGameData();
  }, [loadGameData]);

  // Polling subscription management
  useEffect(() => {
    if (!loadingStages.gameBasics && gameIDNumber && ethersContract) {
      // Subscribe to blockchain updates using BlockchainDataManager
      console.log('[CoinFlip] Setting up blockchain update subscription');

      const handleGameInstanceUpdate = (data) => {
        // Filter updates for this specific game
        if (data.source === 'polling' && (data.contract === 'CoinFlip' || data.contract === 'GameMaker')) {
          console.log(`[CoinFlip] Polling update from ${data.contract}, refreshing game state...`);
          handleGameUpdate('PollingUpdate');
        }
      };

      const unsubscribe = blockchainDataManager.subscribe('gameInstancesUpdated', handleGameInstanceUpdate);

      return unsubscribe;
    }
  }, [loadingStages.gameBasics, gameIDNumber, ethersContract, handleGameUpdate]);

  // Setup polling system when component loads
  useEffect(() => {
    if (!loadingStages.gameBasics && ethersContract) {
      const setupPolling = async () => {
        try {
          await blockchainDataManager.setupSmartPolling();
          blockchainDataManager.addToPolledContracts('CoinFlip');
          console.log('[CoinFlip] Polling system fully initialized with 2 contracts');
        } catch (error) {
          console.error('[CoinFlip] Failed to setup polling system:', error);
        }
      };
      setupPolling();
    }
  }, [loadingStages.gameBasics, ethersContract]);

  const getStatusMessage = (status) => {
    switch (status) {
      case '0': return 'Player 1 Turn';
      case '1': return 'Player 2 Turn';
      case '2': return 'Both Players Turn';
      case '3': return 'Game Ended - Draw';
      case '4': return 'Game Ended - Player 1 Won';
      case '5': return 'Game Ended - Player 2 Won';
      case '6': return 'Game Ended - Killed';
      default: return 'Unknown Status';
    }
  };

  const getCoinResultText = (coinResult) => {
    if (coinResult === null || coinResult === undefined) return '';
    return coinResult === 0 ? 'Heads' : 'Tails';
  };


  const formatEth = (wei) => {
    return ethersUtils.fromWei(wei.toString(), 'ether');
  };


  const getUserRole = () => {
    const isSamePlayer = gameState.player1.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer1 = userState.userAddress.toLowerCase() === gameState.player1.toLowerCase();
    const isPlayer2 = userState.userAddress.toLowerCase() === gameState.player2.toLowerCase();
    
    if (isSamePlayer && (isPlayer1 || isPlayer2)) {
      return 'Both (Heads & Tails)';
    } else if (isPlayer1) {
      return 'Player 1 (Heads)';
    } else if (isPlayer2) {
      return 'Player 2 (Tails)';
    }
    return 'Spectator';
  };

  const getWinnerMessage = () => {
    if (!gameState.gameFinished) return '';
    
    const isSamePlayer = gameState.player1.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer1 = userState.userAddress && userState.userAddress.toLowerCase() === gameState.player1.toLowerCase();
    const isPlayer2 = userState.userAddress && userState.userAddress.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer = isPlayer1 || isPlayer2;
    
    // For players, show simple win/loss message
    if (isPlayer && !isSamePlayer) {
      if (gameState.winner === 'draw') {
        return 'Draw';
      } else if (gameState.winner === gameState.player1) {
        return isPlayer1 ? 'You won' : 'You lost';
      } else if (gameState.winner === gameState.player2) {
        return isPlayer2 ? 'You won' : 'You lost';
      }
    }
    
    // For spectators or same player, show detailed message
    if (gameState.winner === 'draw' && !isSamePlayer) {
      return 'Game ended in a draw';
    } else if (gameState.winner === gameState.player1) {
      if (isSamePlayer) {
        return `You win with ${getCoinResultText(gameState.coinResult)}!`;
      }
      return 'Player 1 wins with Heads!';
    } else if (gameState.winner === gameState.player2) {
      if (isSamePlayer) {
        return `You win with ${getCoinResultText(gameState.coinResult)}!`;
      }
      return 'Player 2 wins with Tails!';
    }
    return '';
  };

  // Show full skeleton while UI is not ready
  if (!uiReady) {
    return <FullGameSkeleton gameType="coinflip" />;
  }

  // Show loading while wallet is initializing
  if (!isWalletInitialized) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Initializing...</h1>
        <p className="text-gray-500">Connecting to wallet...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          Error: {errorMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <style>
        {`
          .collapsible-button {
            background: white !important;
            background-color: white !important;
            background-image: none !important;
            color: #374151 !important;
            border: none !important;
            padding: 0 !important;
            border-radius: 0 !important;
          }
          .collapsible-container {
            background-color: white !important;
            border-radius: 0.5rem !important;
            padding: 1.5rem !important;
            margin-bottom: 1.5rem !important;
            border: none !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          }
          
          /* Shared game message styling */
          .game-message-main {
            font-size: 1.5rem;
            font-weight: 700;
            color: #10b981;
            text-align: center;
            margin-bottom: 0.5rem;
          }
          .game-message-sub {
            font-size: 1rem;
            font-weight: 400;
            color: #374151;
            text-align: center;
          }
          .game-message-pending {
            font-size: 1rem;
            font-weight: 500;
            color: #f59e0b;
            text-align: center;
          }
          
          /* Win/Loss Animations */
          @keyframes celebrate {
            0%, 100% { 
              transform: scale(1);
              box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
            }
            50% { 
              transform: scale(1.02);
              box-shadow: 0 0 15px rgba(16, 185, 129, 0.5);
            }
          }
          
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-2px); }
            50% { transform: translateX(2px); }
            75% { transform: translateX(-1px); }
          }
          
          .win-animation {
            animation: celebrate 1.5s ease-in-out 3;
            border: 3px solid #10b981 !important;
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.4);
          }
          
          .loss-animation {
            animation: shake 0.5s ease-in-out 3;
          }
          
          .winner-highlight {
            border: 3px solid #10b981 !important;
            background: linear-gradient(to right, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05)) !important;
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.3) !important;
          }
          
          .player1-winner {
            border: 3px solid #3b82f6 !important;
            background: linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05)) !important;
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.3) !important;
          }
          
          .player2-winner {
            border: 3px solid #ef4444 !important;
            background: linear-gradient(to right, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05)) !important;
            box-shadow: 0 0 15px rgba(239, 68, 68, 0.3) !important;
          }
        `}
      </style>
      <div className="max-w-4xl mx-auto">
        {/* Top Navigation Bar */}
        <div className="flex justify-between items-center mb-6">
          <Link
            to="/1on1"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition"
          >
            ← Back to Games
          </Link>

          <TopRightBar
            onSwitchWallet={handleSwitchWallet}
            onWalletDisconnect={handleWalletDisconnect}
            showInTestMode={true}
          />
        </div>

        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          CoinFlip Game #{gameID}
        </h1>

        {/* Game Rules - Collapsible */}
        <div className="collapsible-container">
          <button 
            className="collapsible-button"
            onClick={() => setRulesCollapsed(!rulesCollapsed)}
style={{ 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              fontSize: '1.25rem', 
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <span>How CoinFlip Works</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${rulesCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            rulesCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100 mt-3'
          }`}>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li>Player 1 is automatically assigned <strong>Heads</strong></li>
              <li>Player 2 is automatically assigned <strong>Tails</strong></li>
              <li>When the game starts, a coin is immediately flipped</li>
              <li>The player whose choice matches the coin flip wins all the money</li>
              <li>The coin flip uses blockchain randomness for fairness</li>
              <li>🔍 Find the contract info and verified code here: <AddressDisplayLong address={contractAddresses.CoinFlip} useExplorerLink={true} /></li>
            </ul>
          </div>
        </div>

        {/* Game Status */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Game Status</h2>
          {loadingStages.gameBasics ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className="animate-pulse bg-gray-300 h-6 w-32 rounded"></div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Your Role</p>
                <div className="animate-pulse bg-gray-300 h-6 w-24 rounded"></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="text-lg font-medium">{getStatusMessage(gameState.status)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Your Role</p>
                <p className="text-lg font-medium">{getUserRole()}</p>
              </div>
            </div>
          )}
        </div>

        {/* Coin Flip Visual */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700 text-center">Coin Flip Result</h2>
          <div className="flex flex-col items-center">
            <div className={`w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold text-white mb-4 ${
              gameState.coinResult === 0 ? 'bg-yellow-500' : 'bg-gray-600'
            }`}>
              {gameState.coinResult !== null ? getCoinResultText(gameState.coinResult) : '?'}
            </div>
            {gameState.gameFinished && (
              <div className="text-center">
                <p className="text-xl font-semibold text-gray-700">
                  The coin landed on: <span className="text-blue-600">{getCoinResultText(gameState.coinResult)}</span>
                </p>
                <div className={`mt-2 p-3 rounded-lg ${
                  getWinnerMessage() === 'You won' ? 'win-animation bg-green-50' :
                  getWinnerMessage() === 'You lost' ? 'loss-animation bg-red-50' :
                  getWinnerMessage() === 'Draw' ? 'bg-yellow-50' : 'bg-gray-50'
                }`}>
                  <p className={`text-2xl font-bold ${
                    getWinnerMessage() === 'You won' ? 'text-green-600' :
                    getWinnerMessage() === 'You lost' ? 'text-red-600' :
                    getWinnerMessage() === 'Draw' ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {getWinnerMessage()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Players Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Player 1 */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${
            gameState.winner === gameState.player1 ? 'player1-winner' : ''
          }`}>
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 1 (Heads)</h3>
            {loadingStages.gameBasics ? (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <div className="animate-pulse bg-gray-300 h-4 w-full rounded"></div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <div className="animate-pulse bg-gray-300 h-6 w-24 rounded"></div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white font-bold">H</span>
                  </div>
                  <span className="text-gray-700">Always chooses Heads</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player1} useExplorerLink={true} /></p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <p className="text-lg font-medium">{formatEth(gameState.contribution1)} POL</p>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white font-bold">H</span>
                  </div>
                  <span className="text-gray-700">Always chooses Heads</span>
                </div>
                {gameState.winner === gameState.player1 && (
                  <div className="text-blue-600 font-semibold">🎉 Winner!</div>
                )}
              </div>
            )}
          </div>

          {/* Player 2 */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${
            gameState.winner === gameState.player2 ? 'player2-winner' : ''
          }`}>
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 2 (Tails)</h3>
            {loadingStages.gameBasics ? (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <div className="animate-pulse bg-gray-300 h-4 w-full rounded"></div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <div className="animate-pulse bg-gray-300 h-6 w-24 rounded"></div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white font-bold">T</span>
                  </div>
                  <span className="text-gray-700">Always chooses Tails</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player2} useExplorerLink={true} /></p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bet Amount</p>
                  <p className="text-lg font-medium">{formatEth(gameState.contribution2)} POL</p>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white font-bold">T</span>
                  </div>
                  <span className="text-gray-700">Always chooses Tails</span>
                </div>
                {gameState.winner === gameState.player2 && (
                  <div className="text-red-600 font-semibold">🎉 Winner!</div>
                )}
              </div>
            )}
          </div>
        </div>


        {/* Game Details */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-3 text-gray-700">Game Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-600">Game ID</p>
              <p className="font-mono">{gameID}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pot</p>
              <p className="text-lg font-medium">
                {formatEth((parseInt(gameState.contribution1) + parseInt(gameState.contribution2)).toString())} POL
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Move</p>
              <p>{gameState.lastMoveTime > 0 ? new Date(gameState.lastMoveTime * 1000).toLocaleString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Game Contract</p>
              <AddressDisplayLong address={contractAddresses.CoinFlip} useExplorerLink={true} />
            </div>
          </div>
          
          {/* Transaction Details - Unified Component */}
          <GameTransactionHistory
            gameId={gameID}
            gameMakerContract={gameMakerContract}
            gameContract={readOnlyContract}
            gameEvents={coinFlipGameEvents}
            player1={gameState.player1}
            player2={gameState.player2}
          />
        </div>
      </div>
    </div>
  );
};

export default CoinFlipGame;